// domain/constants/authorization.js

/**
 * The calendar a cap applies over. `total` is the whole window and the only non-periodic member,
 * which is what makes `resetBehavior` inapplicable on it.
 *
 * THE VALUES ARE A CROSS-RUNTIME CONTRACT. The billing projector reads them verbatim off the stored
 * document and cannot import from `src/`, so changing a VALUE means the identical change in the
 * backend copy. A value one side does not recognize produces a gate that matches nothing, which
 * reads as "no authorization on file" rather than as a fault.
 *
 * `per_day` ALSO EXISTS IN `UNIT_BASIS`, MEANING SOMETHING ELSE — there one unit IS a day; here the
 * cap refreshes daily. `STATED_UNIT.DAYS` is a third neighbor again. A cross-assignment does not
 * throw, it silently matches nothing.
 *
 * @domainObject value-object
 * @invariant Extension is additive. A new cadence is a new member, never a reshape of caps already
 *   stored under an existing one.
 * @invariant Disjoint from `UNIT_BASIS` and `STATED_UNIT` despite the shared spellings.
 * @typedef {'total' | 'per_day' | 'per_week' | 'per_month'} CapPeriod
 */
export const CAP_PERIOD = {
  TOTAL: 'total',
  PER_DAY: 'per_day',
  PER_WEEK: 'per_week',
  PER_MONTH: 'per_month',
};

/**
 * What happens to a periodic cap's unused capacity when its period rolls over.
 *
 * AN ENUM RATHER THAN A BOOLEAN. The two behaviors look like one flag, but the domain expert
 * flagged payers that average across periods or carry forward under their own rules, so a third
 * member is coming — one line here, a migration of every stored cap with a boolean. A boolean also
 * cannot say "not recorded", which is the state every cap is in today.
 *
 * NEVER DEFAULTED AND NEVER INFERRED FROM THE PERIOD. Periodic caps usually reset and totals usually
 * draw down, but "usually" is what the letter overrides — the whole reason this is recorded.
 *
 * NULL IS A FACT WITH TWO READINGS, told apart by the cap's period: inapplicable on a `total`, and
 * on a periodic cap nobody recorded one, so consumption must HOLD rather than assume.
 *
 * @domainObject value-object
 * @invariant Extension is additive. An averaging or carry-forward variant joins as a new member,
 *   never by redefining `draw_down` to cover it.
 * @invariant Null on a `total` cap. Never null-as-a-substitute for a value a default supplied.
 * @typedef {'reset' | 'draw_down'} ResetBehavior
 */
export const RESET_BEHAVIOR = {
  RESET: 'reset',
  DRAW_DOWN: 'draw_down',
};

/**
 * The unit the LETTER states a cap in.
 *
 * NOT `UNIT_BASIS`, WHICH IS THE CODE'S, and keeping the two apart is the most load-bearing thing in
 * this model. A letter says "20 hours per week"; the code bills in 15-minute units, so the claim
 * carries 80. Both are true and neither derives the other at capture — the conversion belongs to
 * consumption. `visits` has no basis counterpart at all, which is the clearest proof the sets differ
 * rather than one being a superset.
 *
 * `units` IS THE ONE MEMBER THAT IS NOT SELF-DESCRIBING. On a shared limit whose services resolve to
 * codes with different bases the figure is ambiguous in the stored data itself. Recorded as stated
 * and warned about, never blocked: refusing would make a real approval unenterable.
 *
 * @domainObject value-object
 * @invariant Extension is additive, and a new member never migrates into `UNIT_BASIS`. The two sets
 *   answer different questions and are allowed to disagree about what exists.
 * @typedef {'units' | 'hours' | 'sessions' | 'visits' | 'days'} StatedUnit
 */
export const STATED_UNIT = {
  UNITS: 'units',
  HOURS: 'hours',
  SESSIONS: 'sessions',
  VISITS: 'visits',
  DAYS: 'days',
};

/**
 * Where an authorization sits relative to a date, derived from its period and stored nowhere.
 *
 * Deliberately NOT widened to carry whether the number has arrived: those facts vary independently,
 * and one enum would force states that genuinely co-occur to overwrite each other.
 *
 * @domainObject value-object
 * @invariant Derived from the period on every read, never stored. A stored status is a value some
 *   writer has to keep true as the calendar moves, and nothing would.
 * @typedef {'upcoming' | 'active' | 'ended'} AuthorizationStatus
 */
export const AUTHORIZATION_STATUS = {
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
  ENDED: 'ended',
};

export const CAP_PERIOD_VALUES = Object.values(CAP_PERIOD);
export const RESET_BEHAVIOR_VALUES = Object.values(RESET_BEHAVIOR);
export const STATED_UNIT_VALUES = Object.values(STATED_UNIT);

export const isCapPeriod = (value) => CAP_PERIOD_VALUES.includes(value);
export const isResetBehavior = (value) => RESET_BEHAVIOR_VALUES.includes(value);
export const isStatedUnit = (value) => STATED_UNIT_VALUES.includes(value);

/**
 * Does this period refresh, or is it the authorization's whole window?
 *
 * Expressed once rather than as a `!== TOTAL` test at every call site: a second non-periodic member
 * would otherwise have to be found in the validator, the form and consumption alike.
 *
 * @param {string} capPeriod
 * @returns {boolean}
 */
export function isPeriodicCap(capPeriod) {
  return isCapPeriod(capPeriod) && capPeriod !== CAP_PERIOD.TOTAL;
} // isPeriodicCap()
